import { RouteRecordRaw } from 'vue-router';
import type { ExtensionManager } from '@shell/types/extension-manager';
import { PaginationSettingsStores } from '@shell/types/resources/settings';
import { IExtensionProducts } from '@shell/core/plugin-products-external';
import { RouteRecordRawWithParams } from '@shell/core/plugin-types';
import { TypeMapProduct } from '@shell/types/store/type-map';

// Cluster Provisioning types
export * from './types-provisioning';

// package.json metadata
export interface PackageMetadata {
  name: string;
  version: string;
  description: string;
  icon: string;
}

// export interface Route {
//   name: string;
//   path: string;
//   component: Object | Function,
//   children: Route[];
// }

export type PluginRouteRecordRaw = { [key: string]: any }

export type VuexStoreObject = { [key: string]: any }
export type CoreStoreSpecifics = { state: () => VuexStoreObject, getters: VuexStoreObject, mutations: VuexStoreObject, actions: VuexStoreObject }
export type CoreStoreConfig = { namespace: string, baseUrl?: string, modelBaseClass?: string, supportsStream?: boolean, isClusterStore?: boolean }
export type CoreStoreInit = (store: any, ctx: any) => void;
export type RegisterStore = () => (store: any) => void
export type UnregisterStore = (store: any) => void

export type OnEnterLeavePackageConfig = {
  clusterId: string,
  product: string,
  oldProduct: string,
  isExt: string,
  oldIsExt: string
}

export type OnNavToPackage = (store: any, config: OnEnterLeavePackageConfig) => Promise<void>;
export type OnNavAwayFromPackage = (store: any, config: OnEnterLeavePackageConfig) => Promise<void>;
export type OnLogOut = (store: any) => Promise<void>;
export type OnLogIn = (store: any) => Promise<void>;

/**
 * Navigation hooks specified as an object
 */
export type NavHooks = {
  onEnter?: OnNavToPackage,
  onLeave?: OnNavAwayFromPackage,
  onLogout?: OnLogOut,
  onLogin?: OnLogIn,
}

/** Enum regarding the extensible areas/places of the UI */
export enum ExtensionPoint {
  ACTION = 'Action', // eslint-disable-line no-unused-vars
  TAB = 'Tab', // eslint-disable-line no-unused-vars
  PANEL = 'Panel', // eslint-disable-line no-unused-vars
  CARD = 'Card', // eslint-disable-line no-unused-vars
  TABLE_COL = 'TableColumn', // eslint-disable-line no-unused-vars
  TABLE = 'Table', // eslint-disable-line no-unused-vars
}

/** Enum regarding action locations that are extensible in the UI */
export enum ActionLocation {
  HEADER = 'header-action', // eslint-disable-line no-unused-vars
  TABLE = 'table-action', // eslint-disable-line no-unused-vars
}

/** Enum regarding panel locations that are extensible in the UI */
export enum PanelLocation {
  ABOUT_TOP = 'about-top', // eslint-disable-line no-unused-vars
  DETAILS_MASTHEAD = 'details-masthead', // eslint-disable-line no-unused-vars
  DETAIL_TOP = 'detail-top', // eslint-disable-line no-unused-vars
  RESOURCE_LIST = 'resource-list', // eslint-disable-line no-unused-vars
}

/** Enum regarding tab locations that are extensible in the UI */
export enum TabLocation {
  RESOURCE_DETAIL = 'tab', // eslint-disable-line no-unused-vars
  OTHER = 'other-tab-locations', // eslint-disable-line no-unused-vars
  RESOURCE_DETAIL_PAGE = 'resource-detail-page', // eslint-disable-line no-unused-vars
  RESOURCE_CREATE_PAGE = 'resource-create-page', // eslint-disable-line no-unused-vars
  RESOURCE_EDIT_PAGE = 'resource-edit-page', // eslint-disable-line no-unused-vars
  RESOURCE_SHOW_CONFIGURATION = 'resource-show-configuration', // eslint-disable-line no-unused-vars
  CLUSTER_CREATE_RKE2 = 'cluster-create-rke2', // eslint-disable-line no-unused-vars
}

/** Enum regarding card locations that are extensible in the UI */
export enum CardLocation {
  CLUSTER_DASHBOARD_CARD = 'cluster-dashboard-card', // eslint-disable-line no-unused-vars
}

/** Enum regarding table col locations that are extensible in the UI */
export enum TableColumnLocation {
  RESOURCE = 'resource-list', // eslint-disable-line no-unused-vars
}

/** Enum regarding table locations that are extensible in the UI */
export enum TableLocation {
  RESOURCE = 'resource-list', // eslint-disable-line no-unused-vars
}

/** Definition of a Table extension hook */
export type TableAction = {
  tableHook: Function
};

/** Definition of the shortcut object (keyboard shortcuts) */
export type ShortCutKey = {
  windows?: string[];
  mac?: string[];
};

/** Definition of the action options (table actions) */
export type ActionOpts = {
  event: any;
  isAlt: boolean;
  action: any;
};

/** Definition of an extension action (options that can be passed when setting an extension action) */
export type Action = {
  label?: string;
  labelKey?: string;
  tooltipKey?: string;
  tooltip?: string;
  /** Header actions: the tooltip while the action is disabled, saying why. */
  disabledTooltipKey?: string;
  disabledTooltip?: string;
  shortcut?: string | ShortCutKey;
  svg?: Function;
  icon?: string;
  multiple?: boolean;
  /**
   * Whether the action can be used. For a header action, a function returning a boolean is asked
   * again whenever the reactive state it reads changes; one returning a promise, on navigation.
   */
  enabled?: Function | boolean;
  ariaExpanded?: boolean | (() => boolean);
  /**
   * Header actions: no button, only the `shortcut` - for an action that is a keyboard shortcut and
   * nothing else. Defaults to false: the action is a button.
   */
  hidden?: boolean;
  invoke: (opts: ActionOpts, resources: any[], globals?: any) => void | boolean | Promise<boolean>;
};

/** Definition of a panel (options that can be passed when defining an extension panel enhancement) */
export type Panel = {
  component: Function;
};

/** Definition of a card (options that can be passed when defining an extension card enhancement) */
export type Card = {
  label?: string;
  labelKey?: string;
  component: Function;
};

/** Definition of a tab (options that can be passed when defining an extension tab enhancement) */
export type Tab = {
  name: string;
  label?: string;
  labelKey?: string;
  tooltipKey?: string;
  tooltip?: string;
  showHeader?: boolean;
  weight?: number;
  component: Function;
};

/** Definition of the locationConfig object (used in extensions) */
export type LocationConfig = {
  product?: string[],
  resource?: string[],
  namespace?: string[],
  cluster?: string[],
  id?: string[],
  mode?: string[],
  hash?: string[],
  /**
   * path match from URL (excludes host address)
   */
  path?: { [key: string]: string | boolean}[],
  /**
   * Query Params from URL
   */
  queryParam?: { [key: string]: string},
  /**
   * Context specific params.
   *
   * Components can provide additional context specific params that this value must match.
   *
   * Matched as a subset, recursively: only the keys named here have to match, and the context
   * may carry others. Values may be nested objects, so an enhancement can bind to a single
   * entry of a map the component provides (eg. one of a resource's annotations).
   */
  context?: { [key: string]: any },
};

/**
 * Environment metadata that extensions can access
 */
export type ExtensionEnvironment = {
  version: string;
  commit: string;
  isPrime: boolean;
  docsVersion: string; /** e.g. 'v2.10' */
};

/**
 * Configuration required to show a header in a ResourceTable
 */
export interface HeaderOptions {
  /**
   * Order/position of the table column added by an extension
   */
  weight?: number;

  /**
   * Name of the header. This should be unique.
   */
  name?: string;

  /**
   * `false` for a column that isn't data, eg a button: table views leaves it out of Columns, Group
   * By, the query and exports
   */
  tableViews?: boolean;

  /**
   * A string that will show in the table column as a header
   */
  label?: string;

  /**
   * A translation key where the resulting string will show in the table column as a header
   */
  labelKey?: string;

  /**
   * How the cell's value is resolved: either a string path into the row (i.e. `metadata.name`), or
   * a function given the row that returns the value to display. Under server-side pagination a
   * function is display-only - `sort`/`search` must still be string paths to indexed fields.
   */
  value?: string | ((row: any) => any);

  /**
   * A string which represents the path to access the value from the row object which we'll use to sort i.e. `row.meta.value`
   */
  sort?: string | string[] | boolean;

  /**
   * A string which represents the path to access the value from the row object which we'll use to search i.e. `row.meta.value`.
   * A column can name more than one path, as `sort` can. It can be false to disable searching on this field
   */
  search?: string | boolean | string[];

  /**
   * Number of pixels the column should be in the table
   */
  width?: number;

  /**
   * Number of pixels the column's cells narrow to at most, however little room the table has
   */
  minWidth?: number;

  /**
   * Number of pixels the column's cells widen to at most. Longer text wraps
   */
  maxWidth?: number;

  /**
   * Number of lines a cell shows at most. Text that needs more ends in an ellipsis, and the whole
   * text is offered as a tooltip
   */
  lineClamp?: number;

  /**
   * The name of a custom formatter. The available formatters can bee seen in `@rancher/shell/components/formatter`
   */
  formatter?: string;

  /**
   * These options are dependent on the formatter that's chosen. Examples can be seen in `@rancher/shell/components/formatter` and `@rancher/shell/config/table-headers`
   */
  formatterOpts?: any;

  /**
   * Provide a function which accepts a row and returns the value that should be displayed in the column
   * @param row This can be any value which represents the row
   * @returns Can return {@link string | number | null | undefined} to display in the column
   */
  getValue?: (row: any) => string | number | null | undefined;
}

/**
 * Configuration required to show a header in a ResourceTable when server-side pagination is enable
 */
export type PaginationHeaderOptions = Omit<HeaderOptions, 'getValue'>

/**
 * External extension configuration for @HeaderOptions
 */
export type TableColumn = HeaderOptions;

/**
 * External extension configuration for @PaginationHeaderOptions
 */
export type PaginationTableColumn = PaginationHeaderOptions;

/**
 * External extension configuration for @PaginationSettingsStores
 */
export type ServerSidePaginationExtensionConfig = PaginationSettingsStores;

/**
 * Telemetry values returned by an extension's telemetry function - a map of query string param name to value
 */
export type TelemetryParams = { [param: string]: string | number | boolean };

/**
 * Function registered by an extension (via `plugin.register('telemetry', name, fn)`) that returns
 * additional query string params to send with the dynamic content request
 *
 * @param getters Store getters, so that the extension can access the store to generate its values
 */
export type TelemetryFunction = (getters: any) => TelemetryParams;

export interface ConfigureTypeOptions {
  /**
   * Override for the create button string on a list view
   */
  listCreateButtonLabelKey?: boolean;
  /**
   * The resource can edit/show yaml
   */
  canYaml?: boolean;

  /**
   * Modify the way the name looks when displayed
   */
  displayName?: string;

  /**
   * New resources can be created of this type
   */
  isCreatable?: boolean;

  /**
   * Resources of this type can be deleted/removed
   */
  isRemovable?: boolean;

  /**
   * Resources of this type can be edited
   */
  isEditable?: boolean;

  /**
   * This type should be grouped by namespaces when displayed in a table
   */
  namespaced?: boolean;

  /**
   * Show the age column in when displaying this type in a table
   */
  showAge?: boolean;

   /**
   * Show the masthead at the top of the list view of this type
   */
  showListMasthead?: boolean;

   /**
   * Show the state column in when displaying this type in a table
   */
  showState?: boolean;

  /**
   * Define where this type/page should navigate to (menu entry routing)
   */
  customRoute?: Object;

  /**
   * Custom options vary pre resource type
   */
  custom?: any;

  /**
   * Leaving these here for completeness but I don't think these should be advertised as useable to plugin creators.
   */
  // alias
  // depaginate
  // graphConfig
  // hasGraph
  // limit
  // listGroups
  // localOnly
  // location
  // match
  // realResource
  // resource
  // resourceDetail
  // resourceEdit
  // showConfigView
}

export interface ConfigureVirtualTypeOptions extends ConfigureTypeOptions {
  /**
   * Only load the product if the type is present
   */
  ifHave?: string;

  /**
   * Only load the product if the type is present
   */
  ifHaveType?: string | RegExp | Object;

  /**
   * The label that this type should display
   */
  label?: string;

  /**
   * The translation key displayed anywhere this type is referenced
   */
  labelKey?: string;

  /**
   * An identifier that should be unique across all types
   */
  name: string;

  /**
   * Resource types that this nav item owns but does not link to directly. For
   * example, the create page of a resource that has no nav entry of its own.
   */
  navResources?: string[];

  /**
   * The route that this type should correspond to {@link PluginRouteRecordRaw} {@link RouteRecordRaw} {@link RouteRecordRawWithParams}
   */
  route: PluginRouteRecordRaw | RouteRecordRaw | RouteRecordRawWithParams | Object;

  weight?: number;
}

export interface DSLReturnType {
  /**
   * Register multiple types by name and place them all in a group if desired. Primarily used for grouping things in the cluster explorer navigation.
   * @param types A list of types that are going to be registered
   * @param group Conditionally a group you want to places all the types in
   * @returns {@link void}
   */
  basicType: (types: string[] | string, group?: string) => void;

  /**
   * Configure a myriad of options for the specified type
   * @param type The type to be configured
   * @param options {@link ConfigureTypeOptions}
   * @returns {@link void}
   */
  configureType: (type: string, options: ConfigureTypeOptions) => void;

  /**
   * Register the headers/columns that should be used when rendering a table for the specified type.
   * @param type The type you'd like to register headers/columns for.
   * @param headers {@link HeaderOptions[]}
   * @returns {@link void}
   */
  headers: (type: string, headers?: HeaderOptions[], paginationHeaders?: PaginationHeaderOptions[]) => void;

  /**
   * Create and register a new product
   * @param options {@link ProductOptions}
   * @returns {@link void}
   */
  product: (options: TypeMapProduct) => void;

  /**
   /**
   * Remap group display names in the side-menu navigation.
   *
   * Each entry matches a group's internal ID (via string or regex) and replaces its display label
   * with a new name. This only changes how the group is labelled in the UI — it does not move
   * resources between groups.
   *
   * @param match String, string for a regex or a regex object to match against group names
   * @param replace Replacement string or function for the display name
   * @param weight Priority for applying this mapping (higher numbers applied first, default 5)
   * @param continueOnMatch If true, continue matching other rules after this one matches
   * @returns {@link void}
   */
  mapGroup: (match: string | RegExp, replace: string | Function, weight?: number, continueOnMatch?: boolean) => void;

  /**
   * Remap a type ID to a display name
   * @param match String, string for a regex or a regex object to match against type IDs
   * @param replace Replacement string or function for the display name
   * @param weight Priority for applying this mapping (higher numbers applied first, default 5)
   * @param continueOnMatch If true, continue matching other rules after this one matches
   * @returns {@link void}
   */
  mapType: (match: string | RegExp, replace: string | Function, weight?: number, continueOnMatch?: boolean) => void;

  /**
   * Create and configure a myriad of options for a type
   * @param options {@link ConfigureVirtualTypeOptions}
   * @returns {@link void}
   */
  virtualType: (options: ConfigureVirtualTypeOptions) => void;

  /**
   * Side menu ordering for grouping of pages
   * @param input Name of the group
   * @param weight Ordering to be applied for the specified group
   * @param forBasic Apply to basic type instead of regular type tree
   * @returns {@link void}
   */
  weightGroup: (input: string, weight: number, forBasic: boolean) => void;

  /**
   * Side menu ordering for simple pages
   * @param input Name of the page/resource
   * @param weight Ordering to be applied for the specified page/resource
   * @param forBasic Apply to basic type instead of regular type tree
   * @returns {@link void}
   */
  weightType: (input: string, weight: number, forBasic: boolean) => void;

  /**
   * Never show the specified type in the navigation
   * @param regexOrString String, string for a regex or a regex object to match against type names
   * @returns {@link void}
   */
  ignoreType: (regexOrString: string | RegExp) => void;

  /**
   * Never show the specified group or any types in it
   * @param regexOrString String, string for a regex or a regex object to match against group names
   * @param fn Conditional function that accepts getters and returns true if the group should be ignored
   * @returns {@link void}
   */
  ignoreGroup: (regexOrString: string | RegExp, fn?: (getters: any) => boolean) => void;

  /**
   * Move a resource type into a different navigation group
   * @param match String or regex to match against resource type names
   * @param group Target group name to move the matched types into
   * @param weight Ordering weight for the mapping (default: 5)
   * @returns {@link void}
   */
  moveType: (match: string | RegExp, group: string, weight?: number) => void;

  /**
   * Control visibility of bulk actions (e.g. delete) in the list view toolbar for a specific resource type
   * @param type The resource type to configure
   * @param hide Whether to hide bulk actions. Set to `true` to hide them
   * @returns {@link void}
   */
  hideBulkActions: (type: string, hide: boolean) => void;

  labelGroup: (group: string, label: string | undefined, labelKey?: string) => void;

  setGroupDefaultType: (group: string, defaultType: string) => void;
}

/**
 * Context for the constructor of a model extension
 */
export type ModelExtensionContext = {
  /**
   * Dispatch vuex actions
   */
  dispatch: any,
  /**
   * Get from vuex store
   */
  getters: any,
  /**
   * Used to make http requests
   */
  axios: any,
  /**
   * [DEPRECATED] Definition of the extension
   */
  $plugin: ExtensionManager,
  /**
   * Definition of the extension
   */
  $extension: ExtensionManager,
  /**
   * Function to retrieve a localised string
   */
  t: (key: string) => string,
};

/**
 * Constructor signature for a model extension
 */
export type ModelExtensionConstructor = new (context: ModelExtensionContext) => Object;

/**
 * Interface for a UI Extension
 */
export interface IExtension extends IExtensionProducts {

  /**
   * Add a locale to the i18n store
   * @param locale Locale id (e.g. en-us)
   * @param label Label for the locale to be displayed in the i18n chooser
   */
  addLocale(locale: string, label: string): void;

  /**
   * Plugin metadata
   */
  metadata: PackageMetadata;

  /**
   * Validators used in the same manner as shell/utils/custom-validators
   */
  validators: {[key: string]: Function};

  /**
   * Add a module containing localisations for a specific locale
   */
  addL10n(locale: string, fn: Function): void;

  /**
   * Add a route to the Vue Router
   */
  addRoute(route: RouteRecordRawWithParams | RouteRecordRaw): void;
  addRoute(parent: string, route: RouteRecordRawWithParams | RouteRecordRaw): void;

  /**
   * Adds an action/button to the UI
   */
  addAction(where: ActionLocation | string, when: LocationConfig | string, action: Action): void;

  /**
   * Adds a tab to the UI (ResourceTabs component)
   */
  addTab(where: TabLocation | string, when: LocationConfig | string, action: Tab): void;

  /**
   * Adds a panel/component to the UI
   */
  addPanel(where: PanelLocation | string, when: LocationConfig | string, action: Panel): void;

  /**
   * Adds a card to the UI
   */
  addCard(where: CardLocation | string, when: LocationConfig | string, action: Card): void;

  /**
   * Adds a new column to a ResourceTable
   *
   * @param where
   * @param when
   * @param action
   * @param column
   *  The information required to show a header and values for a column in a table
   * @param paginationColumn
   *  As per `column`, but is used where server-side pagination is enabled
   */
  addTableColumn(where: TableColumnLocation | string, when: LocationConfig | string, column: TableColumn, paginationColumn?: TableColumn): void;

  /**
   * Adds to Table events hook on ResourceTable
   *
   * @param where
   * @param when
   * @param action
   */
  addTableHook(where: TableLocation | string, when: LocationConfig | string, action: TableAction): void;

  /**
   * Set the component to use for the landing home page
   * @param component Home page component
   */
  setHomePage(component: any): void;

  /**
   * Add routes to the Vue Router
   */
  addRoutes(routes: PluginRouteRecordRaw[] | RouteRecordRawWithParams[] | RouteRecordRaw[]): void;

   /**
    * Add a hook to be called when the plugin is uninstalled
    * @param hook Function to call when the plugin is uninstalled
    */
  addUninstallHook(hook: Function): void;

  /**
   * Add a generic Vuex Store
   */
  addStore(storeName: string, register: RegisterStore, unregister: UnregisterStore): void;
  /**
   * Add a dashboard Vuex store.
   *
   * This will contain the toolset (getters/mutations/actions/etc) required by the dashboard to support Dashboard components. Most of these
   * will be automatically supplemented when the store is registered, others though will need to be provided to supply package specific
   * functionality (see storeSpecifics). For instance a component may request to fetch all of a resource type which, via a number of generic
   * actions, will eventually call a `request` action which will make the raw http request. This is a pkg specific feature so needs the
   * `request` action needs to be supplied in the `storeSpecifics`
   */
  addDashboardStore(storeName: string, storeSpecifics: CoreStoreSpecifics, config: CoreStoreConfig, init?: CoreStoreInit): void;

  /**
   * Add hooks that will execute when a user navigates
   * - to a route owned by this package
   * - from a route owned by this package
   */
  addNavHooks(
    onEnter?: OnNavToPackage,
    onLeave?: OnNavAwayFromPackage,
    onLogOut?: OnLogOut,
    onLogIn?: OnLogIn,
  ): void;
  addNavHooks(hooks: NavHooks): void;

  enableServerSidePagination(config: ServerSidePaginationExtensionConfig): void;

  /**
   * Adds a model extension
   * @experimental May change or be removed in the future
   *
   * @param type Model type
   * @param clz  Class for the model extension (constructor)
   */
  addModelExtension(type: string, clz: ModelExtensionConstructor): void;

  /**
   * Register 'something' that can be dynamically loaded - e.g. model, edit, create, list, i18n
   *
   * A special type `'l10n-global'` can be used to register a value that will be
   * substituted for `[[name]]` tokens in translation strings, allowing shared
   * terms (e.g. product names) to be defined once and referenced across
   * localisations. The value can be a string or a function returning a string.
   * If no global is registered for a given name, the token's name is used as
   * the value.
   *
   * A special type `'telemetry'` can be used by SUSE extensions to register a function
   * (see `TelemetryFunction`) that returns a map of additional query string params to be
   * sent with the dynamic content request. These params can not overwrite any of the
   * params that are already sent. Telemetry functions from non-SUSE extensions are ignored.
   *
   * @param {String} type type of thing to register, e.g. 'edit'
   * @param {String} name unique name of 'something'
   * @param {Function|string|boolean} fn function that dynamically loads the module for the thing being registered, or (for `l10n-global`) the value itself
   */
  register(type: string, name: string, fn: Function | boolean | string): void;

  /**
   * Will return all of the configuration functions used for creating a new product.
   * @deprecated Should use `addProduct` and `extendProduct` instead and avoid using this directly
   * @param store The store that was passed to the function that's passed to `plugin.addProduct(function)`
   * @param productName The name of the new product. This name is displayed in the navigation.
   */
  DSL(store: any, productName: string): DSLReturnType;

  /**
   * Get information about the Extension Environment
   */
  get environment(): ExtensionEnvironment;
}

/**
 * Legacy interface for a plugin, which is just an extension but with the `DSL` function.
 * @deprecated Should use `IExtension` interface instead
 */
export type IPlugin = IExtension;

// Internal interface
// Built-in extensions may use this, but external extensions should not, as this is subject to change
// Defined as any for now
export type IInternal = any;
